import React, { useEffect, useState, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, Billboard } from '@react-three/drei';
import { Group, Vector3, Mesh, MeshStandardMaterial, TorusGeometry, SphereGeometry } from 'three';
import { Tower as TowerType } from '../store/gameStore';

interface PortalTowerProps {
  tower: TowerType;
}

const PortalTower: React.FC<PortalTowerProps> = ({ tower }) => {
  const [isHovered, setIsHovered] = useState(false);
  const portalRef = useRef<Group>(null);
  const portalRingRef = useRef<Mesh>(null);
  const portalMaterialRef = useRef<MeshStandardMaterial>(null);
  const portalParticlesRef = useRef<Mesh[]>([]);

  // Create portal materials
  const portalMaterial = new MeshStandardMaterial({
    color: '#ffd700',
    metalness: 0.8,
    roughness: 0.2,
    transparent: true,
    opacity: 0.8,
  });

  const particleMaterial = new MeshStandardMaterial({
    color: '#ffd700',
    transparent: true,
    opacity: 0.6,
  });

  // Create portal geometries
  const portalGeometry = new TorusGeometry(1, 0.2, 16, 100);
  const particleGeometry = new SphereGeometry(0.1, 8, 8);

  // Handle portal interaction
  const handlePortalClick = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref') || window.location.origin;
    
    // Construct portal URL with parameters
    const portalUrl = new URL('http://portal.pieter.com');
    portalUrl.searchParams.append('username', 'Player');
    portalUrl.searchParams.append('color', '#ffd700');
    portalUrl.searchParams.append('speed', '5');
    portalUrl.searchParams.append('ref', ref);
    
    // Redirect to portal
    window.location.href = portalUrl.toString();
  };

  // Animate portal effect
  useFrame((state, delta) => {
    if (!portalRef.current || !portalRingRef.current || !portalMaterialRef.current) return;

    // Rotate portal ring
    portalRingRef.current.rotation.y += delta * 0.5;

    // Pulse portal opacity
    portalMaterialRef.current.opacity = 0.8 + Math.sin(state.clock.elapsedTime * 3) * 0.2;

    // Rotate particles
    portalRef.current.rotation.y += delta * 0.5;

    // Animate particles
    portalParticlesRef.current.forEach((particle, i) => {
      const angle = i * (Math.PI / 4) + state.clock.elapsedTime;
      particle.position.x = Math.cos(angle) * 1.5;
      particle.position.z = Math.sin(angle) * 1.5;
    });
  });

  return (
    <group position={tower.position} ref={portalRef}>
      {/* Portal ring */}
      <mesh
        ref={portalRingRef}
        onClick={handlePortalClick}
        onPointerOver={() => setIsHovered(true)}
        onPointerOut={() => setIsHovered(false)}
        geometry={portalGeometry}
        material={portalMaterial}
      />

      {/* Portal label */}
      <Billboard>
        <Text
          position={[0, 2, 0]}
          fontSize={0.5}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {isHovered ? 'Click to Travel' : 'Portal'}
        </Text>
      </Billboard>

      {/* Portal particles */}
      {[...Array(8)].map((_, i) => (
        <mesh
          key={i}
          ref={el => {
            if (el) portalParticlesRef.current[i] = el;
          }}
          geometry={particleGeometry}
          material={particleMaterial}
          position={[
            Math.cos(i * Math.PI / 4),
            0,
            Math.sin(i * Math.PI / 4)
          ]}
        />
      ))}
    </group>
  );
};

export default PortalTower; 